import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.clients_api.client_receipts;


const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'clients_api',
  group: 'client_receipts',
  defaultTtl: '2m',
  hashLargeSegments: true,
  emitMeta: true,
});

/**
 * 
 * from flask_restx import Namespace, Resource, fields
from models import PaymentReceipt
from api.tools.dynamoDB.tables.mp_client_reviews import MPClientReviews

### Namespace for client receipts operations
ns = Namespace('client_receipts', description='Client receipts with reviews')

# Define the review model
review_model = ns.model('Review', {
    'review_id': fields.String(description='The review ID'),
    'rating': fields.Raw(description='The rating data'),
    'comment': fields.String(description='The comment'),
    'data': fields.Raw(description='Full review data')
})

# Define the receipt with review model
receipt_with_review_model = ns.model('ReceiptWithReview', {
    'receipt_id': fields.Integer(description='The receipt ID'),
    'pedido_ids': fields.Raw(description='List of pedido IDs'),
    'mesa_id': fields.Integer(description='Mesa ID'),
    'pedido_type_id': fields.Integer(description='Pedido type ID'),
    'client_name': fields.String(description='Client name'),
    'client_id': fields.Integer(description='Client ID'),
    'mesa_num': fields.String(description='Mesa number'),
    'user_id': fields.Integer(description='User ID'),
    'user_names': fields.Raw(description='List of user names'),
    'local_id': fields.Integer(description='Local ID'),
    'selections': fields.Raw(description='Receipt selections'),
    'subtotal': fields.Float(description='Subtotal amount'),
    'suggested_tip': fields.Float(description='Suggested tip amount'),
    'tip_payed': fields.Float(description='Tip paid amount'),
    'total_payed': fields.Float(description='Total paid amount'),
    'total_due': fields.Float(description='Total due amount'),
    'payed_full': fields.Boolean(description='Whether receipt is fully paid'),
    'timestamp': fields.DateTime(description='Receipt timestamp'),
    'local_timestamp': fields.DateTime(description='Local timestamp'),
    'receipt_number': fields.String(description='Receipt number'),
    'discount_name': fields.String(description='Discount name'),
    'discount_amount': fields.Float(description='Discount amount'),
    'coupon_name': fields.String(description='Coupon name'),
    'coupon_amount': fields.Float(description='Coupon amount'),
    'is_cancelled': fields.Boolean(description='Whether receipt is cancelled'),
    'is_refunded': fields.Boolean(description='Whether receipt is refunded'),
    'session_id': fields.Integer(description='Session ID'),
    'review': fields.Nested(review_model, allow_null=True, description='Client review if exists, null otherwise')
})


@ns.route('/GetReceiptsSummaryByLocalIdAndClientId/<int:local_id>/<int:client_id>')
@ns.param('local_id', 'The local ID')
@ns.param('client_id', 'The client ID')
class GetReceiptsSummaryByLocalIdAndClientId(Resource):
    @ns.doc('Get receipts summary by local ID and client ID with reviews')
    @ns.marshal_list_with(receipt_with_review_model)
    def get(self, local_id, client_id):
        """
        Get all receipts for a specific client at a specific local, including their reviews.
        No JWT authentication required - public endpoint for clients to view their receipts.
        
        Returns a list of receipts with the following structure:
        [
            {
                ...(all receipt fields),
                receipt_id: int,
                review: {
                    review_id: str,
                    rating: dict,
                    comment: str,
                    data: dict
                } | null
            }
        ]
        """
        # Query all receipts for this client and local
        receipts = PaymentReceipt.query.filter_by(
            local_id=local_id,
            client_id=client_id,
            is_cancelled=False  # Exclude cancelled receipts
        ).order_by(PaymentReceipt.timestamp.desc()).all()
        
        if not receipts:
            return [], 200
        
        # Initialize review helper
        review_helper = MPClientReviews()
        
        result = []
        for receipt in receipts:
            # Convert receipt to dictionary
            receipt_data = {
                'receipt_id': receipt.receipt_id,
                'pedido_ids': receipt.pedido_ids,
                'mesa_id': receipt.mesa_id,
                'pedido_type_id': receipt.pedido_type_id,
                'client_name': receipt.client_name,
                'client_id': receipt.client_id,
                'mesa_num': receipt.mesa_num,
                'user_id': receipt.user_id,
                'user_names': receipt.user_names,
                'local_id': receipt.local_id,
                'selections': receipt.selections,
                'subtotal': float(receipt.subtotal) if receipt.subtotal else 0,
                'suggested_tip': float(receipt.suggested_tip) if receipt.suggested_tip else 0,
                'tip_payed': float(receipt.tip_payed) if receipt.tip_payed else 0,
                'total_payed': float(receipt.total_payed) if receipt.total_payed else 0,
                'total_due': float(receipt.total_due) if receipt.total_due else 0,
                'payed_full': receipt.payed_full,
                'timestamp': receipt.timestamp.isoformat() if receipt.timestamp else None,
                'local_timestamp': receipt.local_timestamp.isoformat() if receipt.local_timestamp else None,
                'receipt_number': receipt.receipt_number,
                'discount_name': receipt.discount_name,
                'discount_amount': float(receipt.discount_amount) if receipt.discount_amount else 0,
                'coupon_name': receipt.coupon_name,
                'coupon_amount': float(receipt.coupon_amount) if receipt.coupon_amount else 0,
                'is_cancelled': receipt.is_cancelled,
                'is_refunded': receipt.is_refunded,
                'session_id': receipt.session_id
            }
            
            # Try to get the review for this receipt
            review_data = None
            try:
                success, review_response, status = review_helper.get(receipt.receipt_id, "receipt_id")
                
                if success and review_response:
                    # Extract review data from DynamoDB response
                    # The response should contain the review data
                    if isinstance(review_response, list) and len(review_response) > 0:
                        review_item = review_response[0]
                        review_data = {
                            'review_id': review_item.get('id'),
                            'data': review_item.get('data'),
                            'rating': review_item.get('data', {}) if review_item.get('data') else None,
                            'comment': None  # Reviews might not have a separate comment field
                        }
                    elif isinstance(review_response, dict):
                        review_data = {
                            'review_id': review_response.get('id'),
                            'data': review_response.get('data'),
                            'rating': review_response.get('data', {}) if review_response.get('data') else None,
                            'comment': None
                        }
            except Exception as e:
                # If review fetch fails, just set review to None
                # Don't fail the entire request
                print(f"Error fetching review for receipt {receipt.receipt_id}: {str(e)}")
                review_data = None
            
            # Add review to receipt data
            receipt_data['review'] = review_data
            
            result.append(receipt_data)
        
        return result, 200

 */

export const getReceiptsSummaryByLocalIdAndClientId = async (localId: number, clientId: number) => {
    return await retryWithBackoff(() =>
      cachedApi.get(`/GetReceiptsSummaryByLocalIdAndClientId/${localId}/${clientId}`)
    );
}

